
import { Send, RefreshCw, ShieldAlert, Layers } from "lucide-react";

interface HumanReviewFormProps {
  accuracy: number;
  adherence: number;
  tone: number;
  toolEfficiency: number;
  hallucination: boolean;
  reasoning: string;
  reviewStatus: "completed" | "flagged" | "requires_review";
  submitting: boolean;
  submitError: string | null;
  onAccuracyChange: (val: number) => void;
  onAdherenceChange: (val: number) => void;
  onToneChange: (val: number) => void;
  onToolEfficiencyChange: (val: number) => void;
  onHallucinationChange: (val: boolean) => void;
  onReasoningChange: (val: string) => void;
  onReviewStatusChange: (val: "completed" | "flagged" | "requires_review") => void;
  onCancel: () => void;
  onSubmit: (e: React.FormEvent) => void;
  assignAllEvaluations?: boolean;
  onAssignAllEvaluationsChange?: (val: boolean) => void;
  activeBatchTitle?: string | null;
}

export function HumanReviewForm({
  accuracy,
  adherence,
  tone,
  toolEfficiency,
  hallucination,
  reasoning,
  reviewStatus,
  submitting,
  submitError,
  onAccuracyChange,
  onAdherenceChange,
  onToneChange,
  onToolEfficiencyChange,
  onHallucinationChange,
  onReasoningChange,
  onReviewStatusChange,
  onCancel,
  onSubmit,
  assignAllEvaluations = true,
  onAssignAllEvaluationsChange,
  activeBatchTitle,
}: HumanReviewFormProps) {
  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="p-3 rounded-lg bg-stone-50 dark:bg-stone-950 border border-stone-200 dark:border-stone-800 text-xs text-stone-600 dark:text-stone-400 leading-relaxed">
        Provide calibrated human feedback across each quality dimension. Human reviews carry 70% weight in the composite scorecard.
      </div>

      {/* Dimension Sliders */}
      <div className="space-y-3.5 bg-white dark:bg-stone-900 p-4 rounded-xl border border-stone-200 dark:border-stone-800">
        <div>
          <div className="flex justify-between text-xs mb-1">
            <span className="font-semibold text-stone-800 dark:text-stone-200">Accuracy Score</span>
            <span className="font-mono font-bold text-emerald-600">{accuracy.toFixed(1)} / 5.0</span>
          </div>
          <input
            type="range"
            min="1.0"
            max="5.0"
            step="0.5"
            value={accuracy}
            onChange={(e) => onAccuracyChange(parseFloat(e.target.value))}
            className="w-full accent-emerald-500 cursor-pointer"
          />
        </div>

        <div>
          <div className="flex justify-between text-xs mb-1">
            <span className="font-semibold text-stone-800 dark:text-stone-200">Instruction Adherence</span>
            <span className="font-mono font-bold text-emerald-600">{adherence.toFixed(1)} / 5.0</span>
          </div>
          <input
            type="range"
            min="1.0"
            max="5.0"
            step="0.5"
            value={adherence}
            onChange={(e) => onAdherenceChange(parseFloat(e.target.value))}
            className="w-full accent-emerald-500 cursor-pointer"
          />
        </div>

        <div>
          <div className="flex justify-between text-xs mb-1">
            <span className="font-semibold text-stone-800 dark:text-stone-200">Tone & Courtesy</span>
            <span className="font-mono font-bold text-emerald-600">{tone.toFixed(1)} / 5.0</span>
          </div>
          <input
            type="range"
            min="1.0"
            max="5.0"
            step="0.5"
            value={tone}
            onChange={(e) => onToneChange(parseFloat(e.target.value))}
            className="w-full accent-emerald-500 cursor-pointer"
          />
        </div>

        <div>
          <div className="flex justify-between text-xs mb-1">
            <span className="font-semibold text-stone-800 dark:text-stone-200">Tool Efficiency</span>
            <span className="font-mono font-bold text-emerald-600">{toolEfficiency.toFixed(1)} / 5.0</span>
          </div>
          <input
            type="range"
            min="1.0"
            max="5.0"
            step="0.5"
            value={toolEfficiency}
            onChange={(e) => onToolEfficiencyChange(parseFloat(e.target.value))}
            className="w-full accent-emerald-500 cursor-pointer"
          />
        </div>
      </div>

      {/* Hallucination Toggle */}
      <div className="p-3.5 rounded-xl border border-stone-200 dark:border-stone-800 flex items-center justify-between">
        <div>
          <div className="text-xs font-bold text-stone-900 dark:text-stone-100 flex items-center gap-1.5">
            <ShieldAlert className={`w-3.5 h-3.5 ${hallucination ? "text-rose-500" : "text-stone-400"}`} />
            <span>Hallucination Detected</span>
          </div>
          <p className="text-[11px] text-stone-400 mt-0.5">
            Forces score cap at 1.00 and flags conversation turn.
          </p>
        </div>
        <input
          type="checkbox"
          checked={hallucination}
          onChange={(e) => onHallucinationChange(e.target.checked)}
          className="w-4 h-4 accent-rose-600 rounded cursor-pointer"
        />
      </div>

      {/* Status */}
      <div>
        <label className="block text-xs font-semibold text-stone-700 dark:text-stone-300 mb-1">
          Evaluation Status
        </label>
        <select
          value={reviewStatus}
          onChange={(e) => onReviewStatusChange(e.target.value as any)}
          className="w-full rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 p-2 text-xs text-stone-900 dark:text-stone-100 focus:outline-hidden"
        >
          <option value="completed">Completed (Passing)</option>
          <option value="flagged">Flagged (Requires Remediation)</option>
          <option value="requires_review">Requires Additional Review</option>
        </select>
      </div>

      {/* Batch Assignment Toggle */}
      <div className="p-3.5 rounded-xl border border-stone-200 dark:border-stone-800 bg-stone-50/50 dark:bg-stone-950/50 flex items-center justify-between">
        <div className="pr-2">
          <div className="text-xs font-bold text-stone-900 dark:text-stone-100 flex items-center gap-1.5">
            <Layers className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400 shrink-0" />
            <span>Assign all evaluations to batch</span>
          </div>
          <p className="text-[11px] text-stone-500 dark:text-stone-400 mt-0.5">
            {activeBatchTitle
              ? `Attaches this review and all thread evaluations to batch "${activeBatchTitle}".`
              : "Attaches this review and all thread evaluations to the currently active batch."}
          </p>
        </div>
        <input
          type="checkbox"
          checked={assignAllEvaluations}
          onChange={(e) => onAssignAllEvaluationsChange?.(e.target.checked)}
          className="w-4 h-4 accent-emerald-600 rounded cursor-pointer shrink-0"
        />
      </div>

      {/* Reasoning */}
      <div>
        <label className="block text-xs font-semibold text-stone-700 dark:text-stone-300 mb-1">
          Reasoning &amp; Remediation Notes
        </label>
        <textarea
          rows={4}
          value={reasoning}
          onChange={(e) => onReasoningChange(e.target.value)}
          placeholder="Detail exact rule non-compliance, missing knowledge, or tone discrepancy..."
          className="w-full rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 p-2.5 text-xs text-stone-900 dark:text-stone-100 placeholder:text-stone-400 focus:outline-hidden"
        />
      </div>

      {submitError && (
        <div className="p-3 rounded-lg bg-rose-50 dark:bg-rose-950/60 border border-rose-200 dark:border-rose-800 text-rose-700 dark:text-rose-300 text-xs">
          {submitError}
        </div>
      )}

      <div className="flex items-center gap-2 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 py-2 rounded-lg border border-stone-200 dark:border-stone-800 text-xs font-medium text-stone-700 dark:text-stone-300 hover:bg-stone-50 dark:hover:bg-stone-800"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={submitting}
          className="flex-1 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-semibold text-xs transition flex items-center justify-center gap-1.5 disabled:opacity-50"
        >
          {submitting ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
          <span>Save Review</span>
        </button>
      </div>
    </form>
  );
}
